import { MapPin, Phone, Mail, Check } from 'lucide-react';
import { AnchorLink, Eyebrow, MaskLines, Reveal } from './Reveal';

const SOLUTIONS = ['Recruitment & Talent Acquisition', 'One-Day & Flexible Staffing', 'Payroll & Workforce Management'];

const STATS = [
  { value: '40+', label: 'Clients served', testid: 'stat-clients' },
  { value: '300+', label: 'People deployed through Sania', testid: 'stat-deployed' },
  { value: '159', label: 'One Day Hiring deployments in month one', testid: 'stat-deployments' },
];

export default function Hero() {
  return (
    <section id="home" data-testid="hero-section" className="relative overflow-hidden scroll-mt-28">
      {/* backdrop flourishes */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 right-[-8%] h-[420px] w-[420px] rounded-full bg-lime/20 blur-3xl" />
        <div className="absolute bottom-[-30%] left-[-10%] h-[480px] w-[480px] rounded-full bg-royal/10 blur-3xl" />
        <span className="absolute right-6 top-24 hidden font-display text-[180px] font-extrabold leading-none text-outline lg:block">
          01
        </span>
      </div>

      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-12 sm:px-6 lg:pb-24 lg:pt-16">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow>Welcome to Sania Job Bowl Pvt. Ltd.</Eyebrow>
          </Reveal>

          <h1
            data-testid="hero-title"
            className="mt-6 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-[3.9rem]"
          >
            <MaskLines
              delay={0.15}
              highlightIndex={2}
              lines={['Integrated recruitment,', 'staffing & workforce', 'solutions']}
            />
          </h1>

          <Reveal delay={0.35}>
            <p className="mt-6 inline-flex items-baseline gap-3 font-display text-xl font-bold italic text-royal sm:text-2xl">
              <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rotate-45 bg-lime" />
              A smile in Every Job.
            </p>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Sania Job Bowl connects employers with the right people through recruitment, flexible staffing and
              managed payroll. Our seven-vertical ecosystem connects hiring, workforce administration, skills,
              education and career opportunities. With 40+ clients served and 300+ people deployed through our
              operations, we combine practical coordination with a people-first approach.
            </p>
          </Reveal>

          <Reveal delay={0.45}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <AnchorLink
                href="#contact"
                testid="hero-cta-primary"
                className="rounded-full bg-royal px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white shadow-[0_16px_32px_-12px_rgba(45,58,151,0.65)] transition-[transform,background-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-royal-dark hover:shadow-[0_20px_40px_-12px_rgba(45,58,151,0.7)]"
              >
                Share Your Requirement →
              </AnchorLink>
              <AnchorLink
                href="#services"
                testid="hero-cta-secondary"
                className="rounded-full border border-slate-300 bg-white px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-ink transition-[border-color,transform,background-color] duration-200 hover:-translate-y-0.5 hover:border-royal/50 hover:bg-porcelain"
              >
                Explore Our Solutions
              </AnchorLink>
            </div>
          </Reveal>

          <Reveal delay={0.55}>
            <ul className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-semibold text-slate-500">
              <li className="inline-flex items-center gap-2" data-testid="hero-contact-location">
                <MapPin className="h-4 w-4 text-royal" aria-hidden="true" /> Bengaluru, India
              </li>
              <li>
                <a href="tel:+916361186321" data-testid="hero-contact-phone" className="inline-flex items-center gap-2 transition-colors duration-200 hover:text-royal">
                  <Phone className="h-4 w-4 text-royal" aria-hidden="true" /> +91 63611 86321
                </a>
              </li>
              <li>
                <a href="mailto:connect@saniajobbowl.com" data-testid="hero-contact-email" className="inline-flex items-center gap-2 transition-colors duration-200 hover:text-royal">
                  <Mail className="h-4 w-4 text-royal" aria-hidden="true" /> connect@saniajobbowl.com
                </a>
              </li>
            </ul>
          </Reveal>

          <Reveal delay={0.6}>
            <div className="mt-10 flex items-center gap-4" data-testid="hero-people-first">
              <span aria-hidden="true" className="h-px w-12 bg-lime" />
              <div>
                <p className="font-display text-lg font-extrabold text-ink sm:text-xl">People first. Business forward.</p>
                <p className="mt-0.5 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Recruitment · Staffing · Payroll
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Three core solutions */}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="rounded-[2rem] border border-slate-200/80 bg-white p-6 shadow-card sm:p-8" data-testid="core-solutions-list">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">
              Three core business solutions
            </p>
            <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {SOLUTIONS.map((s, i) => (
                <li
                  key={s}
                  className="group flex items-center gap-3 rounded-2xl bg-porcelain px-4 py-3.5 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-lime-soft"
                >
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-royal text-white">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
                  </span>
                  <span className="font-display text-sm font-bold text-ink sm:text-[15px]">{s}</span>
                  <span className="ml-auto font-mono text-[11px] font-semibold text-slate-400">0{i + 1}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      {/* Stats band */}
      <div className="relative mx-auto mt-14 max-w-7xl px-4 sm:px-6 lg:mt-20 lg:px-8">
        <Reveal>
          <div className="grain-dark relative overflow-hidden rounded-[2rem] bg-royal-ink px-6 py-12 sm:px-10 lg:px-14" data-testid="stats-band">
            <div aria-hidden="true" className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-royal/60 blur-3xl" />
            <div className="relative grid grid-cols-1 gap-10 sm:grid-cols-3">
              {STATS.map((s, i) => (
                <div key={s.testid} data-testid={s.testid} className={`${i > 0 ? 'sm:border-l sm:border-white/15 sm:pl-10' : ''}`}>
                  <p className="font-display text-5xl font-extrabold tracking-tight text-white lg:text-6xl">
                    {s.value}
                    <span aria-hidden="true" className="text-lime">.</span>
                  </p>
                  <p className="mt-2 max-w-[220px] text-sm font-semibold leading-snug text-white/70">{s.label}</p>
                </div>
              ))}
            </div>
            <p className="relative mt-10 border-t border-white/10 pt-5 text-xs leading-relaxed text-white/50" data-testid="stats-disclaimer">
              Company-reported figures as of October 2026. Deployment totals are operational counts supplied by
              Sania; individual assignment counts may include repeat deployments.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
